<script setup>
import { computed, ref } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['import']);
const { t } = useI18n();

const uiFlags = useMapGetter('inboxes/getUIFlags');
const isImportingChat = computed(() => uiFlags.value.isImporting);

const dialogRef = ref(null);
const fileInput = ref(null);

const hasSelectedFile = ref(null);
const selectedFileName = ref('');

const csvUrl = '/downloads/import-chats-sample.csv';

const handleFileClick = () => fileInput.value?.click();

const processFileName = fileName => {
  const lastDotIndex = fileName.lastIndexOf('.');
  const extension = fileName.slice(lastDotIndex);
  const baseName = fileName.slice(0, lastDotIndex);

  return baseName.length > 20
    ? `${baseName.slice(0, 20)}...${extension}`
    : fileName;
};

const handleFileChange = () => {
  const file = fileInput.value?.files[0];
  hasSelectedFile.value = file;
  selectedFileName.value = file ? processFileName(file.name) : '';
};

const handleRemoveFile = () => {
  hasSelectedFile.value = null;
  if (fileInput.value) {
    fileInput.value.value = null;
  }
  selectedFileName.value = '';
};

const uploadFile = async () => {
  if (!hasSelectedFile.value) return;
  emit('import', hasSelectedFile.value);
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :confirm-button-label="t('INBOX_MGMT.IMPORT_CHAT_HISTORY.IMPORT')"
    :disable-confirm-button="isImportingChat"
    :is-loading="isImportingChat"
    :title="t('INBOX_MGMT.IMPORT_CHAT_HISTORY.TITLE')"
    @confirm="uploadFile"
  >
    <template #description>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('INBOX_MGMT.IMPORT_CHAT_HISTORY.DESCRIPTION') }}
        <a
          :href="csvUrl"
          class="text-n-blue-text"
          download="import-chats-sample.csv"
          rel="noopener noreferrer"
          target="_blank"
        >
          {{ t('INBOX_MGMT.IMPORT_CHAT_HISTORY.DOWNLOAD_LABEL') }}
        </a>
      </p>
    </template>

    <div class="flex flex-col gap-2">
      <div class="flex items-center gap-2">
        <label class="text-sm text-n-slate-12 whitespace-nowrap">
          {{ t('INBOX_MGMT.IMPORT_CHAT_HISTORY.LABEL') }}
        </label>
        <div class="flex items-center justify-between w-full gap-2">
          <span v-if="hasSelectedFile" class="text-sm text-n-slate-12">
            {{ selectedFileName }}
          </span>
          <Button
            v-if="!hasSelectedFile"
            :label="t('INBOX_MGMT.IMPORT_CHAT_HISTORY.CHOOSE_FILE')"
            class="!w-fit"
            color="slate"
            icon="i-lucide-upload"
            size="sm"
            variant="ghost"
            @click="handleFileClick"
          />
          <div v-else class="flex items-center gap-1">
            <Button
              :label="t('INBOX_MGMT.IMPORT_CHAT_HISTORY.CHANGE')"
              color="slate"
              size="sm"
              variant="ghost"
              @click="handleFileClick"
            />
            <div class="w-px h-3 bg-n-strong" />
            <Button
              color="slate"
              icon="i-lucide-trash"
              size="sm"
              variant="ghost"
              @click="handleRemoveFile"
            />
          </div>
        </div>
      </div>
    </div>
    <input
      ref="fileInput"
      accept="text/csv"
      class="hidden"
      type="file"
      @change="handleFileChange"
    />
  </Dialog>
</template>
